import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button, LoadingBlock } from '../components/ui.tsx';
import { WorkPage, WorkSection, WorkEmpty, StaffNavigation } from '../components/work-page.tsx';
import {
  SwapApprovalCard,
  SwapStatusChip,
  SwapVisitLine,
  personName,
  useSwapTime,
  type OfficeSwapListing,
} from '../components/shift-swaps.tsx';
import type { SwapItem, SwapListing } from '@shared/shift-swaps.ts';

/** Staff › Swaps, next to Time off: what was asked of me, what I asked, and — for managers — what waits on them. */
export default function StaffSwapsPage() {
  const s = useSession();
  return (
    <Content
      key={`${s.user?.companyId}:${s.user?.id}:${s.currentLocation?.id}:${s.user?.role}:${s.user?.permissions?.join(',')}:${s.company?.planFeatures?.join(',')}`}
    />
  );
}

const denied = (error: unknown) => [401, 402, 403, 404].includes((error as { status?: number }).status ?? 0);

function Content() {
  const { t } = useTranslation();
  const time = useSwapTime();
  const [list, setList] = useState<SwapListing | null>(null);
  const [queue, setQueue] = useState<OfficeSwapListing | null>(null);
  /** Why the approval queue could not be read; shown inside "Waiting on you". */
  const [queueError, setQueueError] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  const generation = useRef(0);

  useEffect(() => {
    alive.current = true;
    void refresh();
    return () => {
      alive.current = false;
      generation.current++;
    };
  }, []);

  /** The two reads stand alone: a failed approval queue never takes the person's own swaps with it. */
  async function refresh() {
    const gen = ++generation.current;
    const current = () => alive.current && gen === generation.current;
    const reason = (caught: unknown) => (denied(caught) ? t('shiftSwaps.unavailable') : t('shiftSwaps.readFailed'));
    let result: SwapListing;
    try {
      result = await api.get<SwapListing>('/staff/swaps');
    } catch (caught) {
      if (!current()) return;
      setList(null);
      setQueue(null);
      setQueueError('');
      setError(reason(caught));
      return;
    }
    if (!current()) return;
    setList(result);
    if (!result.canManage) {
      setQueue(null);
      setQueueError('');
      return;
    }
    try {
      const office = await api.get<OfficeSwapListing>('/swaps?status=pending_owner');
      if (!current()) return;
      setQueue(office);
      setQueueError('');
    } catch (caught) {
      if (!current()) return;
      setQueue(null);
      setQueueError(reason(caught));
    }
  }

  async function act(path: string, body: unknown) {
    if (busy) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await api.post(path, body);
      if (!alive.current) return;
      setMessage(t('shiftSwaps.saved'));
    } catch (caught) {
      if (!alive.current) return;
      setError(denied(caught) ? t('shiftSwaps.unavailable') : t('shiftSwaps.actionFailed'));
    } finally {
      if (alive.current) {
        setBusy(false);
        await refresh();
      }
    }
  }

  async function more() {
    if (!list?.nextCursor || busy) return;
    const gen = generation.current;
    setBusy(true);
    try {
      const result = await api.get<SwapListing>(`/staff/swaps?before=${encodeURIComponent(list.nextCursor)}`);
      if (alive.current && gen === generation.current)
        setList((old) => old && { ...result, open: old.open, items: [...old.items, ...result.items] });
    } catch {
      if (alive.current && gen === generation.current) setError(t('shiftSwaps.readFailed'));
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  /** The next page of the approval queue: `/swaps?status=pending_owner&before=<nextCursor>`. */
  async function moreQueue() {
    if (!queue?.nextCursor || busy) return;
    const gen = generation.current;
    setBusy(true);
    try {
      const result = await api.get<OfficeSwapListing>(
        `/swaps?status=pending_owner&before=${encodeURIComponent(queue.nextCursor)}`,
      );
      if (alive.current && gen === generation.current)
        setQueue(
          (old) =>
            old && {
              ...result,
              items: [...old.items, ...result.items],
              reviews: { ...old.reviews, ...result.reviews },
            },
        );
    } catch {
      if (alive.current && gen === generation.current) setError(t('shiftSwaps.readFailed'));
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  // Open swaps come with the first page whatever the history paging, so an
  // open request is never hidden behind "Load more"; newest first, once each.
  const all = new Map([...(list?.open ?? []), ...(list?.items ?? [])].map((item) => [item.id, item]));
  const items = [...all.values()].sort((left, right) => (left.id < right.id ? 1 : -1));
  const incoming = items.filter((item) => item.incoming);
  const outgoing = items.filter((item) => item.own);

  function row(item: SwapItem) {
    const other = item.own
      ? t('shiftSwaps.to', { name: personName(item.acceptedBy ?? item.target, t('shiftSwaps.anyone')) })
      : t('shiftSwaps.from', { name: personName(item.requester, t('shiftSwaps.former')) });
    return (
      <article key={item.id} className="work-row mb-3 space-y-2" data-swap-id={item.id}>
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-bold">{other}</h3>
          <SwapStatusChip status={item.status} />
          <span className="badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">
            {item.offeredVisit ? t('shiftSwaps.trade') : t('shiftSwaps.giveAway')}
          </span>
        </div>
        <SwapVisitLine label={t('shiftSwaps.visit')} visit={item.visit} />
        {item.offeredVisit ? <SwapVisitLine label={t('shiftSwaps.inExchange')} visit={item.offeredVisit} /> : null}
        {item.acceptedBy && item.own ? (
          <p>{t('shiftSwaps.acceptedBy', { name: personName(item.acceptedBy, t('shiftSwaps.former')) })}</p>
        ) : null}
        {item.note ? <p className="whitespace-pre-wrap break-words">{item.note}</p> : null}
        {item.decisionNote ? <p className="whitespace-pre-wrap break-words">{item.decisionNote}</p> : null}
        {item.status === 'canceled' && item.closeReason === 'visit_changed' ? (
          <p className="text-sm text-[color:var(--color-muted)]">{t('shiftSwaps.visitChangedHint')}</p>
        ) : null}
        {item.canRespond || item.canCancel ? (
          <p className="text-sm text-[color:var(--color-muted)]">
            {t('shiftSwaps.closes', { time: time(item.expiresAt, item.visit.timezone) })}
          </p>
        ) : null}
        {item.canRespond && !item.target ? (
          <p className="text-sm text-[color:var(--color-muted)]">{t('shiftSwaps.anyoneHint')}</p>
        ) : null}
        <div className="flex flex-wrap gap-3">
          {/* Accepting is refused while swaps are off; declining a named request is not. */}
          {item.canRespond && list?.policy !== 'off' ? (
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => void act(`/staff/swaps/${item.id}/respond`, { response: 'accept' })}
            >
              {t('shiftSwaps.accept')}
            </Button>
          ) : null}
          {item.canRespond && item.target ? (
            <Button
              disabled={busy}
              onClick={() => void act(`/staff/swaps/${item.id}/respond`, { response: 'decline' })}
            >
              {t('shiftSwaps.decline')}
            </Button>
          ) : null}
          {item.canCancel ? (
            <Button disabled={busy} onClick={() => void act(`/staff/swaps/${item.id}/cancel`, {})}>
              {t('shiftSwaps.cancel')}
            </Button>
          ) : null}
        </div>
      </article>
    );
  }

  return (
    <WorkPage
      title={t('shiftSwaps.title')}
      description={t('shiftSwaps.description')}
      actions={
        <Button
          disabled={busy}
          onClick={() => {
            setError('');
            void refresh();
          }}
        >
          {t('shiftSwaps.refresh')}
        </Button>
      }
    >
      <StaffNavigation active="swaps" canManage={list?.canManage} ownDocuments={!list?.canManage} />
      {error && <Alert tone="error">{error}</Alert>}
      {message && <Alert tone="success">{message}</Alert>}
      {!list && !error && <LoadingBlock label={t('shiftSwaps.loading')} />}
      {list && (
        <>
          {list.policy === 'off' ? <Alert tone="info">{t('shiftSwaps.off')}</Alert> : null}
          {queue || queueError ? (
            <WorkSection title={t('shiftSwaps.waiting')}>
              {queueError ? <Alert tone="error">{queueError}</Alert> : null}
              {queue && !queue.items.length && <WorkEmpty title={t('shiftSwaps.emptyWaiting')} />}
              {queue?.items.map((item) => (
                <div key={item.id} className="mb-3">
                  <SwapApprovalCard
                    item={item}
                    review={queue.reviews[item.id]}
                    busy={busy}
                    onDecide={(decision, note) => void act(`/swaps/${item.id}/decision`, { decision, note })}
                  />
                </div>
              ))}
              {queue?.nextCursor && (
                <Button disabled={busy} onClick={() => void moreQueue()}>
                  {t('shiftSwaps.loadMore')}
                </Button>
              )}
            </WorkSection>
          ) : null}
          <WorkSection title={t('shiftSwaps.incoming')}>
            {!incoming.length && <WorkEmpty title={t('shiftSwaps.emptyIncoming')} />}
            {incoming.map(row)}
          </WorkSection>
          <WorkSection title={t('shiftSwaps.outgoing')}>
            {!outgoing.length && <WorkEmpty title={t('shiftSwaps.emptyOutgoing')} />}
            {outgoing.map(row)}
          </WorkSection>
          {/* Older history for both lists; every open swap is already above. */}
          {list.nextCursor && (
            <Button disabled={busy} onClick={() => void more()}>
              {t('shiftSwaps.loadMore')}
            </Button>
          )}
        </>
      )}
    </WorkPage>
  );
}
